import React, { useState } from 'react';
import {
  Box,
  Card,
  Button,
  Avatar,
  CircularProgress,
  Tooltip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  connectToIntegration,
  disconnectIntegration
} from '../../api/connector';
import { useSnackbar } from '../../context/SnackbarContext';
import { EPipedreamConnectErrorType } from '../../types/pipedream';
import PlanRestrictionContactModal from '../modal/PlanRestrictionContactModal';
import {
  isEnterprisePlanRestrictionError,
  isEnterprisePlanRestrictionMessage
} from '../../utils/planRestrictionContactModal';

interface IntegrationCardProps {
  id: string;
  name: string;
  logo: string;
  isConnected: boolean;
  organizationId: string;
  connectionId?: string;
  refreshConnectedAccounts: () => Promise<void>;
  disabled?: boolean;
  disabledReason?: string;
}

const IntegrationCard: React.FC<IntegrationCardProps> = ({
  id,
  name,
  logo,
  isConnected,
  organizationId,
  connectionId,
  refreshConnectedAccounts,
  disabled = false,
  disabledReason
}) => {
  const [loading, setLoading] = useState(false);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [planModalDetail, setPlanModalDetail] = useState<string | null>(null);
  const { showSnackbar } = useSnackbar();

  const closePlanModal = () => {
    setPlanModalOpen(false);
    setPlanModalDetail(null);
  };

  const handleConnect = async () => {
    if (!organizationId) {
      showSnackbar(
        'Organization not loaded. Please refresh the page.',
        'error'
      );
      return;
    }

    setLoading(true);
    try {
      await connectToIntegration(id, organizationId);
      await refreshConnectedAccounts();
      showSnackbar(`${name} connected successfully`, 'success');
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : String(err ?? '');

      if (
        errorMessage === EPipedreamConnectErrorType.USER_CANCELLED ||
        errorMessage.includes(EPipedreamConnectErrorType.USER_CANCELLED)
      ) {
        showSnackbar('Connection canceled by user', 'warning');
      } else if (
        errorMessage.includes(EPipedreamConnectErrorType.CONNECTION_FAILED)
      ) {
        const actualError =
          errorMessage.split(':').slice(1).join(':') || 'Unknown error';
        const detail = actualError.trim() || errorMessage;
        if (
          isEnterprisePlanRestrictionMessage(errorMessage) ||
          isEnterprisePlanRestrictionMessage(actualError)
        ) {
          setPlanModalDetail(detail);
          setPlanModalOpen(true);
        } else {
          showSnackbar(`Failed to connect ${name}: ${actualError}`, 'error');
        }
      } else if (isEnterprisePlanRestrictionError(err)) {
        setPlanModalDetail(errorMessage || null);
        setPlanModalOpen(true);
      } else {
        showSnackbar(
          `Failed to connect ${name}: ${errorMessage || 'Unknown error'}`,
          'error'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!connectionId) return;
    setLoading(true);
    try {
      await disconnectIntegration(connectionId);
      await refreshConnectedAccounts();
      showSnackbar(`${name} disconnected successfully`, 'success');
    } catch (err: unknown) {
      if (isEnterprisePlanRestrictionError(err)) {
        setPlanModalDetail(
          err instanceof Error ? err.message : String(err ?? '')
        );
        setPlanModalOpen(true);
      } else {
        showSnackbar(`Failed to disconnect ${name}`, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const cardContent = (
    <Card
      sx={{
        width: '100%',
        minWidth: 100,
        maxWidth: 100,
        height: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px',
        position: 'relative',
        border: isConnected ? '2px solid #4caf50' : '1px solid #e0e0e0',
        '&:hover': {
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
          transform: 'translateY(-2px)',
          transition: 'all 0.2s ease-in-out'
        }
      }}
    >
      {isConnected && (
        <CheckCircleIcon
          sx={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            color: '#4caf50',
            fontSize: '20px'
          }}
        />
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flex: 1
        }}
      >
        <Avatar
          src={logo}
          alt={`${name} logo`}
          variant="square"
          sx={{ width: '60px', height: '60px', mb: 1 }}
        />
      </Box>

      <Button
        variant={isConnected ? 'outlined' : 'text'}
        color={isConnected ? 'error' : 'primary'}
        onClick={isConnected ? handleDisconnect : handleConnect}
        disabled={loading || disabled}
        fullWidth
        sx={{
          mt: 'auto',
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          padding: '4px 0px'
        }}
      >
        {loading ? (
          <CircularProgress size={16} />
        ) : isConnected ? (
          'Disconnect'
        ) : (
          'Connect'
        )}
      </Button>
    </Card>
  );

  const modal = (
    <PlanRestrictionContactModal
      open={planModalOpen}
      onClose={closePlanModal}
      detail={planModalDetail}
    />
  );

  if (disabled && disabledReason) {
    return (
      <>
        <Tooltip title={disabledReason}>
          <Box component="span" sx={{ display: 'inline-block', width: '100%' }}>
            {cardContent}
          </Box>
        </Tooltip>
        {modal}
      </>
    );
  }
  return (
    <>
      {cardContent}
      {modal}
    </>
  );
};

export default IntegrationCard;
